import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import productUseCaseV2 from '../../factories/v2/product';

// Query keys
export const REACT_QUERY_GET_PRODUCTS_V2_QUERY_KEY = 'getProductsV2';
export const REACT_QUERY_GET_PRODUCT_V2_QUERY_KEY = 'getProductV2';
export const REACT_QUERY_GET_BILLZ_SYNC_STATUS_QUERY_KEY = 'getBillzSyncStatus';

/**
 * Get products list (V2)
 */
export const useGetProductsV2 = (variables, options = {}) => {
  const query = useQuery({
    queryKey: [REACT_QUERY_GET_PRODUCTS_V2_QUERY_KEY, variables],
    queryFn: () => productUseCaseV2.getProducts(variables),
    ...options,
  });

  return query;
};

/**
 * Get single product by ID (V2)
 */
export const useGetProductV2 = (id, options = {}) => {
  const query = useQuery({
    queryKey: [REACT_QUERY_GET_PRODUCT_V2_QUERY_KEY, id],
    queryFn: () => productUseCaseV2.getProductById(id),
    enabled: !!id,
    ...options,
  });

  return query;
};

/**
 * Get products list with infinite scroll (V2)
 */
export const useGetProductsV2Infinite = (variables, options = {}) => {
  const query = useInfiniteQuery({
    queryKey: [REACT_QUERY_GET_PRODUCTS_V2_QUERY_KEY, 'infinite', variables],
    queryFn: ({ pageParam = 0 }) => {
      if (pageParam === null) {
        return { products: [], meta: {} };
      }

      return productUseCaseV2.getProducts({
        ...variables,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      const { offset, limit, total } = lastPage.meta || {};

      const nextOffset = offset + limit;

      if (nextOffset < total) {
        return nextOffset;
      }

      return null;
    },
    ...options,
  });

  return query;
};

/**
 * Create product (V2)
 */
export const useCreateProductV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: productUseCaseV2.createProduct,
    onSuccess: (product) => {
      // Invalidate product lists to refetch
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_PRODUCTS_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Update product (V2)
 */
export const useUpdateProductV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ productId, data }) => productUseCaseV2.updateProduct(productId, data),
    onSuccess: (product, variables) => {
      // Invalidate specific product and lists
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_GET_PRODUCT_V2_QUERY_KEY, variables.productId],
      });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_PRODUCTS_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Delete product (V2)
 */
export const useDeleteProductV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: productUseCaseV2.deleteProduct,
    onSuccess: () => {
      // Invalidate product lists
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_PRODUCTS_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Sync products from Billz
 */
export const useSyncBillzProductsV2 = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: productUseCaseV2.syncBillzProducts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_GET_PRODUCTS_V2_QUERY_KEY] });
    },
  });

  return mutation;
};

/**
 * Poll Billz sync status
 */
export const useGetBillzSyncStatusV2 = (options = {}) => {
  return useQuery({
    queryKey: [REACT_QUERY_GET_BILLZ_SYNC_STATUS_QUERY_KEY],
    queryFn: productUseCaseV2.getBillzSyncStatus,
    ...options,
  });
};

/**
 * Upload single image
 */
export const useUploadImageV2 = () => {
  return useMutation({
    mutationFn: productUseCaseV2.uploadImage,
  });
};

/**
 * Upload multiple images
 */
export const useUploadMultipleImagesV2 = () => {
  return useMutation({
    mutationFn: productUseCaseV2.uploadMultipleImages,
  });
};

/**
 * Get AI-generated product content (title, description, metafields) from image
 */
export const useGetProductAiSuggestionV2 = () => {
  return useMutation({
    mutationFn: productUseCaseV2.getProductAiSuggestion,
  });
};
